<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="primary">
                <ion-buttons slot="start">
                    <ion-back-button text="Retour" :default-href="`/report-sheets/${sheet?.uuid}`"></ion-back-button>
                </ion-buttons>
                <ion-title>Rapport : {{ sheet?.title }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <div :class="$style['sub-header']">Matériel ({{ sheet?.equipments.length ?? 0 }})</div>

        <ion-content>
            <ContentSidebarWrapper side="left">
                <template v-slot:content>
                    <div v-if="!loaded">
                        <EquipmentSkeleton/>
                    </div>
                    
                    <div v-else-if="sheet?.equipments?.length" style="padding-bottom: 200px;">
                        <ion-accordion-group>
                            <ion-accordion v-for="equipment in sheet?.equipments" :key="equipment.uuid" :value="equipment.uuid">
                                <ion-item slot="header" color="light">
                                    <ion-label>
                                        {{ equipment?.reference }} - {{ equipment?.name }}
                                        <span v-if="equipment.total_cost " :class="$style['total_cost']">Total : {{ equipment.total_cost ?? 0.00 }}€</span>
                                    </ion-label>
                                </ion-item>
                                
                                <div slot="content">
                                    <ion-item>
                                        <UnitySelector @ionChange="handleUnity(equipment, $event)" :disabled="fieldDisabled" v-model="equipment.unity" />
                                    </ion-item>

                                    <ion-item>
                                        <ion-input :disabled="fieldDisabled" label="Quantité :" placeholder="Enter data" v-model="equipment.quantity" @change="calculateTotalDailyCost(equipment)"></ion-input>
                                    </ion-item>

                                    <ion-item>
                                        <ion-input @click="triggerUserSelector(equipment)" :disabled="fieldDisabled" label="Employé :" placeholder="Select data" :value="`${equipment.employee_firstname} ${equipment.employee_lastname}`"></ion-input>

                                        <span v-if="equipment.employee_firstname || equipment.employee_lastname" :class="$style['container-button-remove-employee']">
                                            <ion-button fill="outline" size="small" @click="removeEmployee(equipment)" slot="end">
                                                <ion-icon :class="$style['icon-remove-employee']" :icon="personRemoveOutline"></ion-icon>
                                            </ion-button>
                                        </span>
                                    </ion-item>

                                    <ion-item  v-if="user.rights?.includes('report_sheets.manager')">
                                        <ion-input :disabled="fieldDisabled" :label="equipment.unity === 'Hr' ? 'Coût horaire' : 'Coût unitaire'" v-model="equipment.hourly_cost" type="number" @change="calculateTotalDailyCost(equipment)"></ion-input>
                                    </ion-item>

                                    <ion-item v-if="user.rights?.includes('report_sheets.manager')">
                                        <ion-input :disabled="true" label="Coût total" v-model="equipment.total_cost"></ion-input>
                                    </ion-item>

                                    <div v-if="!fieldDisabled" :class="$style['container-button']">
                                        <ion-button v-if="false" slot="start" size="small" fill="outline" @click="presentActionEquipment(equipment)">Supprimer</ion-button>
                                        <ion-button slot="end" size="small" color="success" @click="formSubmit(equipment)">Enregistrer</ion-button>
                                    </div>
                                </div>
                            </ion-accordion>
                        </ion-accordion-group>
                    </div>
                    <div v-else>
                        <ion-text class="ion-text-center" color="medium">
                            <p>Aucun résultat</p>
                        </ion-text>
                    </div>
                </template>

                <template v-slot:sidebar v-if="loaded && sheet.total_equipments_cost">
                    <p :class="$style['total_day']">Coût total du matériel : {{ sheet.total_equipments_cost ?? 0.00 }}€</p>
                </template>
            </ContentSidebarWrapper>

            <template v-if="!sheet?.equipments_step_validated || userCanForceEdit()">
                <ion-fab slot="fixed" vertical="bottom" horizontal="end">
                    <ion-fab-button color="primary" @click="presentActionSheet">
                        <ion-icon :icon="caretUp"></ion-icon>
                    </ion-fab-button>
                </ion-fab>

                <EquipmentSelectorButton style="display: none;" ref="equipmentSelectorRef" @change="addEquipment($event.value)" :filter="{ types: ['small-equipment', 'rolling-stock', 'supply'] }" :bypass-organization="true"/>
                <UserSelectorButton style="display: none;" ref="userSelectorRef" @change="selectEmployee($event.value)" :bypass-organization="true" />
            </template>
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
    import {
        IonBackButton,
        IonButtons,
        IonHeader,
        IonPage,
        IonToolbar,
        IonItem,
        IonContent,
        toastController,
        IonFab,
        IonFabButton,
        actionSheetController,
        alertController,
        IonIcon,
        IonAccordionGroup,
        IonAccordion,
        IonInput,
    } from "@ionic/vue"

    import {
        caretUp, personRemoveOutline
    } from 'ionicons/icons'

    import { defineComponent } from "vue"
    import EquipmentSkeleton from "@/components/skeletons/EquipmentSkeleton.vue";
    import ContentSidebarWrapper from "@/components/wrappers/ContentSidebarWrapper.vue"
    import { ReportSheet } from "@/report-sheets"
    import EquipmentSelectorButton from "@/components/data/selectors/EquipmentSelectorButton.vue"
    import { Equipment } from "@/equipments"
    import { useUserStore } from "@/stores/user"
    import UserSelectorButton from "@/components/data/selectors/UserSelectorButton.vue";
    import { User } from "@/users";
    import UnitySelector from "@/components/forms/ui/UnitySelector.vue";

    export default defineComponent({
        components: {
            EquipmentSelectorButton,
            EquipmentSkeleton,
            ContentSidebarWrapper,
            UserSelectorButton,
            UnitySelector,
            IonBackButton,
            IonButtons,
            IonHeader,
            IonItem,
            IonPage,
            IonToolbar,
            IonContent,
            IonFab,
            IonFabButton,
            IonIcon,
            IonAccordionGroup,
            IonAccordion,
            IonInput
        },
        setup() {
            const user = useUserStore()

            return {
                caretUp,
                user,
                personRemoveOutline
            }
        },
        data() {
            return {
                fieldDisabled: true,
                loaded: false as boolean | false,
                sheet: null as ReportSheet | null,
                equipmentEditingUuid: null as string | null
            }
        },
        mounted() {
            if (this.user.is_logged_in)
                this.load()

            this.$watch("user.is_logged_in", (is_logged_in: boolean) => {
                if (is_logged_in) this.load();
            });
        },
        methods: {
            calculateTotalDailyCost(equipment: Equipment) {
                const hourlyCost = equipment.hourly_cost ? parseFloat(equipment.hourly_cost) : 0
                const quantity = equipment.quantity ? parseFloat(equipment.quantity) : 0

                const totalCost = quantity * hourlyCost
                equipment.total_cost = totalCost.toFixed(2)
            },
            removeEmployee(equipment: Equipment) {
                equipment.employee_firstname = ''
                equipment.employee_lastname = ''
            },
            selectEmployee(user: User) {
                if (this.sheet) {
                    const index = this.sheet.equipments.findIndex((e: Equipment) => e.uuid === this.equipmentEditingUuid)

                    this.sheet.equipments[index].employee_firstname = user.first_name ?? ''
                    this.sheet.equipments[index].employee_lastname = user.last_name ?? ''
                }
            },
            triggerUserSelector(equipment: Equipment) {
                this.equipmentEditingUuid = equipment.uuid

                const userSelector = this.$refs.userSelectorRef as any
                
                if (userSelector?.openSelector)
                    userSelector.openSelector()
            },
            handleUnity(equipment: Equipment, event: any) {
                equipment.quantity = event.detail.value === 'Hr' ? '8' : '1'

                this.calculateTotalDailyCost(equipment)
            },
            load() {
                this.loaded = false
                
                this.axios.get(`/report-sheets/${this.$route.params.uuidReport}`)
                .then((responses) => {
                    this.sheet = responses.data

                    if (this.sheet && (!this.sheet.equipments_step_validated || this.userCanForceEdit())) 
                        this.fieldDisabled = false

                    if (this.sheet) {
                        this.sheet.equipments = this.sheet.equipments.map((equipment: Equipment) => {
                            equipment.employee_firstname = equipment.employee_firstname ?? ''
                            equipment.employee_lastname = equipment.employee_lastname ?? ''

                            return equipment
                        })
                    }
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            userCanForceEdit(): boolean {
                if (!this.sheet || !this.user) return false
                
                // Get user's role for this report sheet
                const userRole = this.sheet.roles?.find(role => role.user?.uuid === this.user.uuid)?.role
                
                if (!userRole) return false
                
                // Site-supervisor and manager can always modify, regardless of validation status
                if (['site-supervisor', 'manager'].includes(userRole)) {
                    return true
                }
                
                // Site-manager can modify if the report is not validated by site-manager or manager
                if (userRole === 'site-manager') {
                    return !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
                }
                
                // Other roles can only modify if no validation has been done
                return !this.sheet.validated_by_site_supervisor && !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
            },
            formSubmit(equipment: Equipment) {
                if (!this.loaded)
                    return
                
                this.loaded = false

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/equipments/${equipment.uuid}`, {
                    unity: equipment?.unity,
                    quantity: equipment?.quantity,
                    employee_firstname: equipment?.employee_firstname,
                    employee_lastname: equipment?.employee_lastname,
                    hourly_cost: equipment?.hourly_cost
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Correctly updated',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                     this.load()
                })
            },
            addEquipment(equipment: Equipment) {
                this.loaded = false

                this.axios.post(`/report-sheets/${this.$route.params.uuidReport}/equipments`, {
                    equipmentUuid: equipment.uuid
                }).then(async (response) => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Equipment added',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    if (this.sheet) {
                        const equipment = response.data
                        equipment.employee_firstname = ''
                        equipment.employee_lastname = ''
                    }
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.load()
                })
            },
            stepValidation() {
                this.loaded = false

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/step-validation`, {
                    step: 'equipments_step_validated'
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Equipments step validated',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    this.$router.push(`/report-sheets/${this.$route.params.uuidReport}`)
                }).catch((error) => {
                    console.error(error)
                    this.loaded = true
                })
            },
            triggerEquipmentSelector() {
                const equipmentSelector = this.$refs.equipmentSelectorRef as any

                if (equipmentSelector?.openSelector)
                    equipmentSelector.openSelector()
            },
            deleteItem(equipment: Equipment) {
                if (!this.loaded)
                    return

                this.loaded = false
                
                this.axios.delete(`/report-sheets/${this.$route.params.uuidReport}/equipments/${equipment.uuid}`)
                .then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Correctly deleted',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    if (this.sheet)
                        this.sheet.equipments = this.sheet.equipments.filter((e: Equipment) => e.uuid !== equipment.uuid)
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            async presentActionEquipment(equipment: Equipment) {
                const alert = await alertController.create({
                    message: 'Etes-vous sûr de vouloir supprimer définitivement ?',
                    htmlAttributes: {
                        'aria-label': 'alert dialog'
                    },
                    buttons: [
                        {
                            text: 'Annuler',
                            role: "cancel"
                        },
                        {
                            text: 'Confirmer',
                            role: "confirm",
                            handler: () => {
                                this.deleteItem(equipment)
                            }
                        }
                    ]
                })

                await alert.present()
            },
            async presentActionSheet() {
                const actionSheet = await actionSheetController.create({
                    buttons: [
                        {
                            text: "Ajouter un matériel",
                            handler: () => {
                                this.triggerEquipmentSelector()
                            }
                        },
                        {
                            text: "Valider cette étape",
                            handler: async () => {
                                const alert = await alertController.create({
                                    message: 'Etes-vous sûr de vouloir valider cette étape ?',
                                    htmlAttributes: {
                                        'aria-label': 'alert dialog'
                                    },
                                    buttons: [
                                        {
                                            text: 'Annuler',
                                            role: "cancel"
                                        },
                                        {
                                            text: 'Confirmer',
                                            role: "confirm",
                                            handler: () => {
                                                this.stepValidation()
                                            }
                                        }
                                    ]
                                })

                                await alert.present()
                            }
                        },
                        {
                            text: "Annuler",
                            role: "cancel"
                        }
                    ]
                })

                await actionSheet.present()
            }
        }
    })
</script>

<style lang="scss" module>
    .sub-header {
        background: #000;
        color:white;
        height: 50px;
        width: 100%;
        display: grid;
        align-content: center;
        justify-content: center;
    }
    ion-item {
        --inner-padding-end: 0;
        --padding-end: 16px;
    }
    ion-select {
        justify-content: flex-start;
    }
    .container-button {
        width: 100%;
        display: flex;
        justify-content: space-between;
        padding: 12px;
    }
    .total_cost {
        display: block;
        margin-top: .25em;
        color: var(--ion-color-medium);
    }
    .total_day {
        color: #fff;
        background: #9e9e9e;
        margin: 0;
        padding: 10px;
        text-align: center;
    }
</style>